import { useEffect, useState } from "react";

const API_URL = "/api/service_user_billing_rules.php";

export const yearMonthInJapan = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:"Asia/Tokyo", year:"numeric", month:"2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part=>[part.type, part.value]));
  return `${values.year}-${values.month}`;
};

const emptyRule = () => ({ item_code:"", item_label:"", started_ym:yearMonthInJapan(), ended_ym:"" });

async function request(path = "", method = "GET", body = null) {
  const options = { method, credentials:"include", headers:{ "Content-Type":"application/json" } };
  if (body) options.body = JSON.stringify(body);
  const response = await fetch(`${API_URL}${path}`, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "請求ルールの取得に失敗しました");
  return data;
}

const inputStyle = {
  width:"100%", boxSizing:"border-box", border:"1px solid #cbd5e1", borderRadius:7,
  padding:"8px 9px", fontSize:13, background:"#fff",
};

function RuleForm({ initial, serviceUserId, onCancel, onSaved }) {
  const [form, setForm] = useState(() => initial ? {
    item_code:initial.item_code,
    item_label:initial.item_label,
    started_ym:initial.started_ym,
    ended_ym:initial.ended_ym || "",
  } : emptyRule());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async event => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, serviceUserId, id:initial?.id };
      const data = await request("", initial ? "PUT" : "POST", payload);
      onSaved(data.billing_rule);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return <form onSubmit={submit} style={{ marginTop:12, padding:12, border:"1px solid #bfdbfe", borderRadius:9, background:"#eff6ff" }}>
    <div style={{ fontSize:13, fontWeight:800, marginBottom:9 }}>{initial ? "請求ルールを編集" : "請求ルールを追加"}</div>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))", gap:9 }}>
      <label style={{ fontSize:11, color:"#475569" }}>請求項目名
        <input aria-label="請求項目名" value={form.item_label} onChange={event=>setForm(current=>({ ...current, item_label:event.target.value }))} placeholder="例：家賃" maxLength={100} required style={inputStyle}/>
      </label>
      <label style={{ fontSize:11, color:"#475569" }}>項目コード
        <input aria-label="項目コード" value={form.item_code} onChange={event=>setForm(current=>({ ...current, item_code:event.target.value.toLowerCase() }))} placeholder="例：rent" pattern="[a-z][a-z0-9_]{0,49}" maxLength={50} required style={inputStyle}/>
      </label>
      <label style={{ fontSize:11, color:"#475569" }}>適用開始月
        <input aria-label="適用開始月" type="month" value={form.started_ym} onChange={event=>setForm(current=>({ ...current, started_ym:event.target.value }))} required style={inputStyle}/>
      </label>
      <label style={{ fontSize:11, color:"#475569" }}>適用終了月（任意）
        <input aria-label="適用終了月" type="month" value={form.ended_ym} min={form.started_ym} onChange={event=>setForm(current=>({ ...current, ended_ym:event.target.value }))} style={inputStyle}/>
      </label>
    </div>
    <div style={{ marginTop:6, fontSize:11, color:"#64748b" }}>項目コードは将来の実費入力・月次確認との照合に使用します。同じ内容には同じコードを使用してください。</div>
    {error && <div role="alert" style={{ color:"#b91c1c", fontSize:12, marginTop:8 }}>{error}</div>}
    <div style={{ display:"flex", gap:8, marginTop:10 }}>
      <button type="submit" disabled={saving}>{saving ? "保存中…" : "保存"}</button>
      <button type="button" onClick={onCancel}>キャンセル</button>
    </div>
  </form>;
}

export default function BillingRulesPanel({ serviceUser }) {
  const [rules, setRules] = useState([]);
  const [capabilities, setCapabilities] = useState({ can_view:false, can_manage:false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    request(`?serviceUserId=${encodeURIComponent(serviceUser.id)}`)
      .then(data=>{
        if (!active) return;
        setRules(data.billing_rules || []);
        setCapabilities(data.capabilities || { can_view:true, can_manage:false });
        setError("");
      })
      .catch(requestError=>active && setError(requestError.message))
      .finally(()=>active && setLoading(false));
    return () => { active = false; };
  }, [serviceUser.id]);

  const applySaved = saved => {
    setRules(current => current.some(rule=>Number(rule.id) === Number(saved.id))
      ? current.map(rule=>Number(rule.id) === Number(saved.id) ? saved : rule)
      : [saved, ...current]);
    setCreating(false);
    setEditing(null);
  };

  const endRule = async rule => {
    const endedYm = window.prompt("適用終了月を入力してください（YYYY-MM）", yearMonthInJapan());
    if (endedYm === null) return;
    setError("");
    try {
      const data = await request("", "PUT", { id:rule.id, serviceUserId:serviceUser.id, action:"end", ended_ym:endedYm });
      applySaved(data.billing_rule);
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  return <section aria-label="請求ルール" style={{ marginTop:14, paddingTop:12, borderTop:"1px solid #e2e8f0" }}>
    <div style={{ display:"flex", justifyContent:"space-between", gap:10, alignItems:"center", flexWrap:"wrap" }}>
      <div>
        <div style={{ fontSize:14, fontWeight:800 }}>毎月確認する請求項目</div>
        <div style={{ fontSize:11, color:"#64748b", marginTop:2 }}>月次の請求漏れ確認に使用するルールです。</div>
      </div>
      {capabilities.can_manage && !creating && <button type="button" onClick={()=>{ setCreating(true); setEditing(null); }}>＋ 請求ルール追加</button>}
    </div>
    {loading && <div style={{ padding:"10px 0", color:"#64748b", fontSize:12 }}>請求ルールを読み込み中…</div>}
    {error && <div role="alert" style={{ color:"#b91c1c", fontSize:12, marginTop:8 }}>{error}</div>}
    {creating && <RuleForm serviceUserId={serviceUser.id} onCancel={()=>setCreating(false)} onSaved={applySaved}/>}
    {!loading && <div style={{ display:"grid", gap:7, marginTop:10 }}>
      {rules.map(rule=><div key={rule.id} style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"9px 11px", background:rule.is_current ? "#f0fdf4" : "#f8fafc" }}>
        <div style={{ display:"flex", justifyContent:"space-between", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <div>
            <span style={{ fontWeight:800, fontSize:13 }}>{rule.item_label}</span>
            <code style={{ marginLeft:7, fontSize:11, color:"#64748b" }}>{rule.item_code}</code>
            <div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>{rule.started_ym} ～ {rule.ended_ym || "継続"} ・ {rule.is_current ? "現在有効" : "適用期間外"}</div>
          </div>
          {capabilities.can_manage && <div style={{ display:"flex", gap:6 }}>
            <button type="button" onClick={()=>{ setEditing(rule); setCreating(false); }}>編集</button>
            {rule.ended_ym === null && <button type="button" onClick={()=>endRule(rule)}>適用終了</button>}
          </div>}
        </div>
        {editing?.id === rule.id && <RuleForm initial={rule} serviceUserId={serviceUser.id} onCancel={()=>setEditing(null)} onSaved={applySaved}/>}
      </div>)}
      {rules.length === 0 && <div style={{ color:"#64748b", fontSize:12, padding:"8px 0" }}>請求ルールはまだ登録されていません。</div>}
    </div>}
  </section>;
}
